import { AdminLayout } from '@components'
import axios from 'axios'
import type { NextPage } from 'next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { TableLoader } from '@loaders'
import { IShareListings } from '@types'
import { formatCurrency, standardDate, openStoredDocument } from '@utils'
import { Country } from 'country-state-city'
import Link from 'next/link'

const ShareListingDetail: NextPage = () => {
  const router = useRouter()
  const { id } = router.query
  const [loading, setLoading] = useState<boolean>(true)
  const [shareListings, setShareListings] = useState<IShareListings>()
  const countries: any[] = Country.getAllCountries()

  useEffect(() => {
    if (!router.isReady) return
    axios
      .get(process.env.NEXT_PUBLIC_API_URL + `/admin/shareListings/${id}`)
      .then(response => {
        setShareListings(response.data)
        setLoading(false)
      })
      .catch(error => {
        setLoading(false)
      })
  }, [router.isReady, id])

  return (
    <AdminLayout>
      {loading && <TableLoader />}

      <div
        className={`${
          loading ? 'opacity-0' : ''
        } mt-8 overflow-hidden bg-white shadow sm:rounded-lg`}>
        <div className="bg-primary px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-white">
            Share Listing Information
          </h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">
                Company name
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.companyName ? shareListings?.companyName : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">Share price</dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.sharePrice
                  ? formatCurrency(Number(shareListings?.sharePrice))
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">
                Number of shares
              </dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.numberOfShares
                  ? shareListings?.numberOfShares
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">
                Last funding round share price
              </dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.lastFundingRoundSharePrice
                  ? formatCurrency(
                      Number(shareListings?.lastFundingRoundSharePrice),
                    )
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">Share type</dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.shareType ? shareListings?.shareType : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">Deadline at</dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.deadlineAt
                  ? standardDate(new Date(shareListings.deadlineAt))
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">
                How did you acquire your shares
              </dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.howAcquireShares
                  ? shareListings?.howAcquireShares
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">
                Is vested shares
              </dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.isVestedShares === true ? 'Yes' : 'No'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">
                Any restriction to sell your shares
              </dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.isAnyRestrictionsToSellYourShares === true
                  ? 'Yes'
                  : 'No'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">Notes</dt>
              <dd className="mt-1 text-sm capitalize text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.notes ? shareListings?.notes : '--'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {shareListings?.documents && shareListings?.documents?.length > 0 && (
        <div
          className={`${
            loading ? 'opacity-0' : ''
          } mt-16 overflow-hidden bg-white shadow sm:rounded-lg`}>
          <div className="bg-primary">
            <dl className="sm:divide-y sm:divide-gray-200">
              <div className="grid grid-cols-3 py-4 sm:gap-4 sm:px-6 sm:py-5 md:grid-cols-3">
                <dt className="text-lg font-medium leading-6 text-white">
                  Document name
                </dt>
                <dt className="text-lg font-medium leading-6 text-white">
                  Document type
                </dt>
                <dt className="text-lg font-medium leading-6 text-white"></dt>
              </div>
            </dl>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
            <dl className="sm:divide-y sm:divide-gray-200">
              {shareListings?.documents ? (
                shareListings?.documents?.map((document, index) => (
                  <div
                    key={index}
                    className="grid-cols-3 py-4 sm:grid sm:gap-4 sm:px-6 sm:py-5 md:grid-cols-3">
                    <dt className="text-sm font-medium uppercase text-gray-500">
                      {document.name ? document.name : '--'}
                    </dt>
                    <dt className="text-sm font-medium uppercase text-gray-500">
                      {document.documentType
                        ? document.documentType.replace(
                            /([a-z0-9])([A-Z])/g,
                            '$1 $2',
                          )
                        : '--'}
                    </dt>
                    <dt className="text-sm font-medium uppercase text-gray-500">
                      {document.url ? (
                        <a
                          className="cursor-pointer text-primary hover:text-primary-light"
                          onClick={() =>
                            openStoredDocument(document.id, { admin: true })
                          }>
                          view
                        </a>
                      ) : (
                        '--'
                      )}
                    </dt>
                  </div>
                ))
              ) : (
                <></>
              )}
            </dl>
          </div>
        </div>
      )}

      <div
        className={`${
          loading ? 'opacity-0' : ''
        } mt-8 overflow-hidden bg-white shadow sm:rounded-lg`}>
        <div className="bg-primary px-4 py-5 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-white">
            User Details
          </h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
          <dl className="sm:divide-y sm:divide-gray-200">
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">User Email</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.profile?.user?.email
                  ? shareListings?.profile?.user?.email
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">First Name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.profile?.user?.firstName
                  ? shareListings?.profile?.user?.firstName
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">Last Name</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.profile?.user?.lastName
                  ? shareListings?.profile?.user?.lastName
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">Country</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.profile?.country
                  ? countries.find(
                      item => item?.isoCode === shareListings?.profile?.country,
                    ).name
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">State</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.profile?.state
                  ? shareListings?.profile?.state
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">City</dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.profile?.city
                  ? shareListings?.profile?.city
                  : '--'}
              </dd>
            </div>
            <div className="py-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
              <dt className="text-sm font-medium text-gray-500">
                Phone number
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:col-span-2 sm:mt-0">
                {shareListings?.profile?.phoneNumber
                  ? shareListings?.profile?.phoneNumber
                  : '--'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </AdminLayout>
  )
}

export default ShareListingDetail
